import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import logo from '../logo.jpg';
import AuthContext from '../contexts/AuthContext';

import PushNotificationService from '../services/PushNotificationService';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  /**
   * 「비밀번호를 잊었습니다」 를 관리자에게 전한 상태.
   *
   * 관리자가 링크를 만들어 줄 때까지 사용자가 할 수 있는 일이 없으면, 사람은
   * 여기서 멈춰 서서 아무에게도 말하지 않는다. 눌러서 손을 들 수 있게 한다.
   */
  const [asked, setAsked] = useState(false);
  const [asking, setAsking] = useState(false);

  const askReset = async () => {
    if (!email.trim()) {
      setError('먼저 이메일을 적어 주세요.');
      return;
    }
    setError('');
    setAsking(true);
    try {
      await api.post('/password-resets/request', { email });
    } catch (err) {
      // 서버가 답을 안 해도 화면은 같은 말을 한다. 계정이 있는지 없는지를
      // 여기서 갈라 보이면 주소를 넣어 보며 누가 쓰는지 알아낼 수 있다.
      console.error('Failed to request a reset', err);
    } finally {
      setAsked(true);
      setAsking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      const me = await login(response.data.access_token, response.data.refresh_token);

      // Initialize Push Notifications AFTER successful login
      try {
        await PushNotificationService.init();
        // Since init() might not re-fire the 'registration' event if already registered, 
        // we explicitly send the stored token to the server now that we have an auth token.
        await PushNotificationService.sendStoredTokenToServer();
      } catch (pushErr) {
        console.error('Failed to init push notifications after login:', pushErr);
      }

      // 근무자에게 회사 전체 이행률을 보여줄 이유가 없다. 자기가 할 일로 보낸다.
      navigate(me?.role === 'STAFF' ? '/today' : '/');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || JSON.stringify(err);
      setError(`로그인 실패: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="card border-0 shadow-lg" style={{ width: '100%', maxWidth: '400px' }}>
        <div className="card-body p-5">
          <div className="text-center mb-4">
            <div className="mb-3">
              <img src={logo} alt="로고" height="100" className="rounded-4" />
            </div>
            <p className="text-muted mt-3">시설 관리 시스템에 오신 것을 환영합니다.</p>
          </div>

          {error && <div className="alert alert-danger text-center">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label text-muted small fw-bold">이메일</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0"><i className="bi bi-envelope text-muted"></i></span>
                <input
                  type="email"
                  className="form-control border-start-0 ps-0"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="form-label text-muted small fw-bold">비밀번호</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0"><i className="bi bi-lock text-muted"></i></span>
                <input
                  type="password"
                  className="form-control border-start-0 ps-0"
                  placeholder="비밀번호 입력"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary w-100 py-2 fw-bold" disabled={loading}>
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </form>

          <div className="text-center mt-4">
            {/* 비밀번호를 잊었을 때 갈 곳이 없으면, 사람은 여기서 멈춰 서서
                아무에게도 말하지 않는다. 누르면 관리자에게 전해진다. */}
            {asked ? (
              <div className="alert alert-success py-2 small mb-2">
                관리자에게 전했습니다. 재설정 링크를 받으시면 새 비밀번호를 정하실 수
                있습니다.
              </div>
            ) : (
              <p className="text-muted small mb-1">
                비밀번호를 잊으셨나요?{' '}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline small text-decoration-underline"
                  onClick={askReset}
                  disabled={asking}
                >
                  {asking ? '전하는 중…' : '관리자에게 재설정 요청'}
                </button>
              </p>
            )}
            {/* 여기서 두 길이 갈린다. 이 갈림을 흐리게 두면, 초대 링크를
                잃어버린 직원이 자기 회사와 **이름만 같은 회사를 하나 더**
                만들고 그 안에 혼자 남는다. 본인은 앱이 비어 있다고 느끼고
                관리자는 그 사람을 찾지 못한다.

                회사 이름이 이미 있는지 알려주는 식으로는 막을 수 없다 --
                어느 회사가 이 서비스를 쓰는지가 새어 나간다. 그래서 말로
                갈라 놓는다: 초대받은 사람은 링크로, 처음 시작하는 회사만
                이 문으로. */}
            <p className="text-muted small mb-1">
              회사에 <strong>초대받으셨나요?</strong>{' '}
              <span className="text-muted">받으신 초대 링크로 들어오세요.</span>
            </p>
            <p className="text-muted small mb-1">
              <Link to="/signup" className="text-decoration-none">
                우리 회사를 새로 등록하기
              </Link>
            </p>
            <div className="mt-3 pt-3 border-top">
              <Link to="/privacy" className="text-decoration-none text-muted extra-small" style={{ fontSize: '0.75rem' }}>개인정보처리방침</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
